'use client';
import React from "react";
import "./keyboard-type.css";
import {useKeyboardState} from "../../KeyboardStateContext";
import {CgMenuBoxed} from "react-icons/cg";
import keyboardSilhouetteLayouts from "../../keyboardSilhouetteLayouts";
import keyboardCustomLayouts from "../../keyboardCustomLayouts";

const KEYBOARD_TYPES = [
    {name: "fullsize", title: "100%", detail: "Full size", count: "104 keys", silhouette: "100%"},
    {name: "eighteenhundred", title: "1800", detail: "", count: "96 keys", silhouette: "1800"},
    {name: "ninetysix", title: "96%", detail: "compact", count: "100 keys", silhouette: "96%"},
    {name: "tenkeyless", title: "80%", detail: "TKL", count: "87 keys", silhouette: "80%"},
    {name: "seventyfive-exploded", title: "75%", detail: "Exploded", count: "82 keys", silhouette: "75%"},
    {name: "seventyfive", title: "75%", detail: "Compact", count: "84 keys", silhouette: "75% compact"},
    {name: "sixtyfive-exploded", title: "65%", detail: "Exploded", count: "67 keys", silhouette: "65%"},
    {name: "sixtyfive", title: "65%", detail: "Compact", count: "68 keys", silhouette: "65% compact"},
    {name: "compact", title: "60%", detail: "", count: "61 keys", silhouette: "60%"},
    {name: "fifty", title: "50%", detail: "", count: "48 keys", silhouette: "50%"},
    {name: "forty", title: "40%", detail: "", count: "41 keys", silhouette: "40%"},
];

const FULL_SIZE_SILHOUETTE_WIDTH = 560.67;
const SILHOUETTE_PREVIEW_SCALE = 1.18;
const keyboardLayoutCoordinates = {
    ...keyboardSilhouetteLayouts,
    ...keyboardCustomLayouts,
};

export const getPhysicalRows = (sections) => (
    sections?.flatMap((keyboardSection) => (
        keyboardSection?.section?.flatMap((section) => section?.row || []) || []
    )) || []
);

export const hasKeyName = (sections, keyName) => getPhysicalRows(sections).some((row) => row?.name === keyName);

export const mapSwitchableBottomRows = (sections, variant) => {
    const useFn = variant === "fn";
    return sections?.map((keyboardSection) => ({
        ...keyboardSection,
        section: keyboardSection?.section?.map((section) => ({
            ...section,
            row: section?.row?.map((row) => {
                if (row?.name !== "ContextMenu" && row?.name !== "Fn") return row;
                return useFn
                    ? {
                        ...row,
                        name: "Fn",
                        keys: ["Fn"],
                        special: false,
                        excluded: true,
                        failed: false,
                        wasPressed: false,
                        isPressed: false,
                    }
                    : {
                        ...row,
                        name: "ContextMenu",
                        keys: [<CgMenuBoxed />],
                        special: true,
                        excluded: false,
                        failed: false,
                        wasPressed: false,
                        isPressed: false,
                    };
            })
        }))
    }));
};

const SilhouettePreview = ({layout}) => {
    const silhouette = keyboardLayoutCoordinates[layout];
    if (!silhouette) return null;
    const keys = silhouette.previewKeys || silhouette.keys;

    return (
        <span
            className="keyboard-type-silhouette"
            style={{
                aspectRatio: silhouette.ratio,
                width: `${Math.min(100, (parseFloat(silhouette.width) / FULL_SIZE_SILHOUETTE_WIDTH) * 100 * SILHOUETTE_PREVIEW_SCALE)}%`,
                "--plate-x": silhouette.plateX,
                "--plate-y": silhouette.plateY,
            }}
            aria-hidden="true"
        >
            {keys.map((key, index) => (
                <span
                    // The silhouette file has no key identity; index is the stable source order.
                    key={index}
                    className="keyboard-type-silhouette-key"
                    style={key}
                />
            ))}
        </span>
    );
};

const KeyboardType = () => {
    const {keyboard, updateKeyboard} = useKeyboardState();
    const activeType = (typeName) => {
        return keyboard?.types?.find(item => item.active)?.name === typeName;
    }

    const makeActiveType = (typeName) => {
        if (!keyboard?.types?.length) return;
        updateKeyboard({
            keyboard: {
                ...keyboard,
                types: keyboard.types.map((item, i) => {
                    if (item.name === typeName) {
                        return {
                            ...item,
                            active: true
                        }
                    }
                    return {
                        ...item,
                        active: false
                    }
                })
            }
        })
    }

    return (
        <>
            <div className="keyboard-type-selector">
                {KEYBOARD_TYPES.map((type) => (
                    <button
                        type="button"
                        key={type.name}
                        className={`keyboard-type ${activeType(type.name) ? "keyboard-type-active" : ""}`}
                        onClick={() => makeActiveType(type.name)}
                    >
                        <span className="keyboard-type-name">
                            <b>{type.title}</b>
                            <span>{type.detail}</span>
                        </span>
                        <span className={`keyboard-type-preview keyboard-type-preview-${type.name} ${activeType(type.name) ? "keyboard-active-type" : ""}`}>
                            <SilhouettePreview layout={type.name} />
                        </span>
                        <span className="keyboard-type-count">{type.count}</span>
                    </button>
                ))}
            </div>
        </>
    );
}

export default KeyboardType;
